import React, { useState, useRef } from 'react';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Upload, FileText, CheckCircle2, Loader2, XCircle, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import { formatCurrency } from '../lib/formatters';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLE = {
  ready: 'text-green-700 bg-green-50',
  duplicate: 'text-amber-700 bg-amber-50',
  unmatched: 'text-red-700 bg-red-50',
  error: 'text-red-700 bg-red-50',
};

const MCATransactionImport = () => {
  const { getAuthHeaders } = useAuth();
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);

  const handlePreview = async () => {
    if (!file) return;
    setLoading(true);
    setPreview(null);
    setResult(null);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await axios.post(`${API}/mca-import/preview`, formData, {
        headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' },
      });
      setPreview(res.data);
      toast.success(`Parsed ${res.data.total_rows.toLocaleString()} rows`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Preview failed');
    } finally { setLoading(false); }
  };

  const handleImport = async () => {
    if (!file) return;
    setImporting(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await axios.post(`${API}/mca-import/execute`, formData, {
        headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' },
        timeout: 300000,
      });
      setResult(res.data);
      toast.success(`Imported ${res.data.inserted.toLocaleString()} transactions`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Import failed');
    } finally { setImporting(false); }
  };

  const s = preview?.summary || {};

  return (
    <Layout>
      <div className="space-y-6 px-4 sm:px-0">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">MCA Transaction Import</h1>
          <p className="text-gray-500 mt-1">One-time bulk import of historical transactions from CSV.</p>
        </div>

        {/* Upload Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Upload className="h-5 w-5 text-blue-600" />
              Upload Transaction CSV
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4">
              <input
                ref={fileRef}
                type="file"
                accept=".csv"
                className="hidden"
                data-testid="csv-file-input"
                onChange={e => { setFile(e.target.files?.[0] || null); setPreview(null); setResult(null); }}
              />
              <Button variant="outline" onClick={() => fileRef.current?.click()} data-testid="select-file-btn">
                <FileText className="h-4 w-4 mr-2" />
                {file ? file.name : 'Select CSV File'}
              </Button>
              {file && (
                <span className="text-sm text-gray-500">{(file.size / 1024 / 1024).toFixed(1)} MB</span>
              )}
              <Button onClick={handlePreview} disabled={!file || loading} data-testid="preview-btn">
                {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ArrowRight className="h-4 w-4 mr-2" />}
                {loading ? 'Parsing...' : 'Preview'}
              </Button>
            </div>
            <p className="text-xs text-gray-400 mt-2">
              Expected columns: Deal ID, Merchant Name, Date of the Transaction, Transaction Amount, Collector Company, Merchant #, Type, Description
            </p>
          </CardContent>
        </Card>

        {/* Summary */}
        {preview && (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
              <Card className="border-gray-200">
                <CardContent className="pt-4 text-center">
                  <p className="text-2xl font-bold" data-testid="total-rows">{preview.total_rows.toLocaleString()}</p>
                  <p className="text-xs text-gray-500">Total Rows</p>
                </CardContent>
              </Card>
              <Card className="border-green-200 bg-green-50/50">
                <CardContent className="pt-4 text-center">
                  <p className="text-2xl font-bold text-green-700" data-testid="ready-count">{(s.ready_to_import || 0).toLocaleString()}</p>
                  <p className="text-xs text-green-600">Ready to Import</p>
                </CardContent>
              </Card>
              <Card className="border-amber-200 bg-amber-50/50">
                <CardContent className="pt-4 text-center">
                  <p className="text-2xl font-bold text-amber-700" data-testid="dupe-count">{(s.duplicates || 0).toLocaleString()}</p>
                  <p className="text-xs text-amber-600">Duplicates (skip)</p>
                </CardContent>
              </Card>
              <Card className="border-red-200 bg-red-50/50">
                <CardContent className="pt-4 text-center">
                  <p className="text-2xl font-bold text-red-700" data-testid="unmatched-count">{(s.unmatched || 0).toLocaleString()}</p>
                  <p className="text-xs text-red-600">Unmatched Deals</p>
                </CardContent>
              </Card>
              <Card className="border-red-200 bg-red-50/50">
                <CardContent className="pt-4 text-center">
                  <p className="text-2xl font-bold text-red-700" data-testid="error-count">{(s.parse_errors || 0).toLocaleString()}</p>
                  <p className="text-xs text-red-600">Parse Errors</p>
                </CardContent>
              </Card>
            </div>

            {/* Type Breakdown */}
            {s.type_breakdown && Object.keys(s.type_breakdown).length > 0 && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">Type Breakdown</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(s.type_breakdown).sort((a, b) => b[1] - a[1]).map(([type, count]) => (
                      <Badge key={type} variant="outline" className="text-sm px-3 py-1">
                        {type}: <span className="font-bold ml-1">{count.toLocaleString()}</span>
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Preview Table */}
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <CardTitle className="text-base">
                    Preview ({Math.min(preview.preview.length, 200)} of {preview.total_preview.toLocaleString()} rows)
                  </CardTitle>
                  <Button
                    onClick={handleImport}
                    disabled={importing || (s.ready_to_import || 0) === 0}
                    className="bg-green-600 hover:bg-green-700"
                    data-testid="import-btn"
                  >
                    {importing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
                    {importing ? 'Importing...' : `Import ${(s.ready_to_import || 0).toLocaleString()} Transactions`}
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-gray-100 sticky top-0">
                        <TableHead className="w-12">Row</TableHead>
                        <TableHead>Deal ID</TableHead>
                        <TableHead>Merchant</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead className="text-right">Amount</TableHead>
                        <TableHead>CSV Type</TableHead>
                        <TableHead>Mapped Type</TableHead>
                        <TableHead>Description</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {preview.preview.map((row, i) => (
                        <TableRow key={`${row.row}-${i}`} className={row.status === 'duplicate' ? 'opacity-50' : row.status === 'error' || row.status === 'unmatched' ? 'bg-red-50/30' : ''}>
                          <TableCell className="text-xs text-gray-400">{row.row}</TableCell>
                          <TableCell className="font-mono font-medium">{row.deal_id}</TableCell>
                          <TableCell className="text-sm max-w-[150px] truncate">{row.merchant_name || row.deal_name || '-'}</TableCell>
                          <TableCell className="text-sm">{row.date ? new Date(row.date).toLocaleDateString() : '-'}</TableCell>
                          <TableCell className={`text-right font-medium ${(row.amount || 0) < 0 ? 'text-red-600' : 'text-green-600'}`}>
                            {row.amount != null ? formatCurrency(row.amount) : '-'}
                          </TableCell>
                          <TableCell className="text-sm">{row.csv_type || '-'}</TableCell>
                          <TableCell>
                            {row.mapped_type ? (
                              <Badge variant="outline" className="text-xs">{row.mapped_type}</Badge>
                            ) : '-'}
                          </TableCell>
                          <TableCell className="text-xs text-gray-500 max-w-[120px] truncate">{row.description || row.notes || row.reason || '-'}</TableCell>
                          <TableCell>
                            <Badge className={`text-xs ${STATUS_STYLE[row.status] || 'bg-gray-50'}`}>
                              {row.status}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </>
        )}

        {/* Import Result */}
        {result && (
          <Card className={result.ok ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'}>
            <CardContent className="pt-6">
              <div className="flex items-start gap-3">
                {result.ok ? <CheckCircle2 className="h-6 w-6 text-green-600 mt-0.5" /> : <XCircle className="h-6 w-6 text-red-600 mt-0.5" />}
                <div>
                  <p className="font-semibold text-lg" data-testid="import-result">Import Complete</p>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-3 text-sm">
                    <div>
                      <p className="text-gray-500">Inserted</p>
                      <p className="text-2xl font-bold text-green-700" data-testid="inserted-count">{result.inserted.toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Skipped (dupes/unmatched)</p>
                      <p className="text-2xl font-bold text-amber-700">{result.skipped.toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Errors</p>
                      <p className="text-2xl font-bold text-red-700">{result.errors.toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Deals Recomputed</p>
                      <p className="text-2xl font-bold text-blue-700" data-testid="recomputed-count">{result.deals_recomputed.toLocaleString()}</p>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
};

export default MCATransactionImport;
